import baselineOnboardingRoutes from '@/pages/baseline/onboarding/routes'
import v1OnboardingRoutes from '@/pages/v1/onboarding/routes'
import v2OnboardingRoutes from '@/pages/v2/onboarding/routes'
import type { ABTestRouteConfig } from '@app-studio-web/ui/types/abtest'

/**
 * A/B Test Configuration for MorphCasa
 *
 * Baseline: Original shorter onboarding flow (control)
 * V1: Extended onboarding with psychology-based pages
 * V2: Deep Psychological Onboarding (Headway-style) - 34 pages for max CVR
 *
 * To add new variants:
 * 1. Create variant directory in pages/{variant}/onboarding/
 * 2. Import variant routes here
 * 3. Add to ABTestRouteConfig.variants
 * 4. Update getABTestConfig() enabledVariants
 */

// Route definition type for extracting paths
interface RouteDefinition {
  path?: string
  children?: RouteDefinition[]
}

// Helper to map onboardingRoutes definitions to an ordered list of child paths
function extractChildPaths(onboardingRoutesDef: RouteDefinition[]): string[] {
  const parent = onboardingRoutesDef[0]
  if (!parent || !parent.children) return []
  return parent.children
    .filter((child: RouteDefinition) => child.path) // Filter out index routes
    .map((child: RouteDefinition) => child.path as string)
}

const baselineOrder = extractChildPaths(
  baselineOnboardingRoutes as RouteDefinition[],
)
const v1Order = extractChildPaths(v1OnboardingRoutes as RouteDefinition[])
const v2Order = extractChildPaths(v2OnboardingRoutes as RouteDefinition[])

export const morphCasaABTestRouteConfig: ABTestRouteConfig = {
  baseline: baselineOrder,
  variants: {
    v1: v1Order,
    v2: v2Order,
  },
}

/**
 * Get A/B test configuration from environment variables
 */
export function getABTestConfig() {
  const clientSecret = import.meta.env.VITE_CONFIDENCE_CLIENT_SECRET as string
  const flagName =
    (import.meta.env.VITE_CONFIDENCE_FLAG_NAME as string) ||
    'morphcasa-onboarding-variant'

  if (!clientSecret) {
    console.warn('No Confidence client secret provided, A/B testing disabled')
    return null
  }

  return {
    clientSecret,
    flagName,
    defaultVariant: 'baseline',
    enabledVariants: ['baseline', 'v1', 'v2'],
  }
}
